/**
 * The "All" row of a multi-select picker (ORCH-418)
 *
 * Purpose: One definition, shared by the flat dropdown (`ListFieldRenderer`) and the
 *          hierarchy dialog, of when the "All" row shows, what it says, what it stores and
 *          how it shows its state. Pure functions, so they're testable without MUI.
 * Rules:  "Select All" stores `[ALL_VALUE]` only when the user sees the whole list. While a
 *          search is active the row is "Select All Matching Results"; when the picker offers
 *          less than the whole list (permission scope, a values limit, narrowing) it is
 *          "Select All Available". Both store the offered ids explicitly, because a part of
 *          the list isn't "all".
 */
import { ALL_VALUE, asList, isAll } from './listValues';

export type SelectAllMode = 'all' | 'available' | 'matching' | 'off';
export type SelectAllState = 'none' | 'some' | 'all';

/** The pseudo-option id the flat dropdown uses for the "All" row. */
export const SELECT_ALL_OPTION_ID = '__select_all__';

/**
 * The row's label. It is an action, so it reads "Select All" (QA follow-up of 2026-10-26); the
 * chip the field shows for a stored ALL stays `ALL_LABEL` ("All").
 */
export const SELECT_ALL_LABELS: Record<Exclude<SelectAllMode, 'off'>, string> = {
  all: 'Select All',
  available: 'Select All Available',
  matching: 'Select All Matching Results',
};

/**
 * Which "All" row a picker shows
 *
 * Input: enabled (multi-select, the field allows All, and no caller turned it off),
 *        narrowed (the picker offers less than the whole list), searching (search text)
 */
export const selectAllMode = (
  enabled: boolean,
  narrowed: boolean,
  searching: boolean
): SelectAllMode => {
  if (!enabled) return 'off';
  if (searching) return 'matching';
  if (narrowed) return 'available';
  return 'all';
};

/**
 * The state of the "All" checkbox
 *
 * Logic: 'all' only when the value is ALL in 'all' mode. In the explicit modes the row is
 *        checked when every offered id is picked. A hand-picked list of every value is
 *        'some' in 'all' mode: an explicit list is never promoted to ALL (Q7).
 */
export const selectAllState = (
  value: unknown,
  offeredIds: string[],
  mode: SelectAllMode = 'all'
): SelectAllState => {
  if (isAll(value)) return 'all';
  const picked = new Set(asList(value));
  if (picked.size === 0) return 'none';
  if (mode !== 'all' && offeredIds.length > 0 && offeredIds.every((id) => picked.has(id))) {
    return 'all';
  }
  return offeredIds.some((id) => picked.has(id)) || mode === 'all' ? 'some' : 'none';
};

/**
 * The value after the "All" row is clicked
 *
 * Logic: 'all' mode toggles between [ALL_VALUE] and blank. The explicit modes add the
 *        offered ids to the selection, or remove them when they're all picked already.
 *        A value of ALL is first expanded to the offered ids so the result is explicit.
 */
export const toggleSelectAll = (
  value: unknown,
  offeredIds: string[],
  mode: SelectAllMode
): string[] => {
  if (mode === 'all') return isAll(value) ? [] : [ALL_VALUE];
  const current = isAll(value) ? [] : asList(value);
  if (selectAllState(value, offeredIds, mode) === 'all') {
    const remove = new Set(offeredIds);
    return current.filter((id) => !remove.has(id));
  }
  const next = [...current];
  offeredIds.forEach((id) => {
    if (!next.includes(id)) next.push(id);
  });
  return next;
};

/**
 * Untick one value while the field is ALL: every other offered value, explicitly (§4 item 2)
 */
export const removeFromAll = (offeredIds: string[], removedId: string): string[] =>
  offeredIds.filter((id) => id !== removedId);
